import type { ReactNode } from "react";
import React, { useRef, useState } from "react";
import { Box, ClickAwayListener, MenuList, Paper, Popper } from "@mui/material";
import type { StyledComponentBase } from "@/components/component-base.types";
import classNames from "classnames";
import type { IDropdownMenuItem } from "./dropdown-menu-item";
import { DropdownMenuItem } from "./dropdown-menu-item";
import styles from "./dropdown-menu.module.scss";

/**
 * Props for the DropdownMenu component.
 */
export interface IDropdownMenuProps extends StyledComponentBase {
  /* The list of menu items to display in the dropdown. */
  items?: IDropdownMenuItem[];

  /* Custom menu content for menus that need richer controls than flat items. */
  renderContent?: (closeMenu: () => void) => ReactNode;

  /* The currently selected value. */
  selectedValue?: string | number;

  /* Placement of the dropdown relative to the trigger component.
    Defaults to "bottom-start". */
  placement?: "bottom-end" | "bottom-start" | "top-end" | "top-start";

  /* If true, the dropdown remains open until a click outside occurs. */
  leaveOpenUntilClickAway?: boolean;

  /* The component that triggers the dropdown (e.g., button, icon). */
  triggerComponent: React.JSX.Element;

  /* Optional callback fired when the menu is opened or closed.
    Receives the open state as a boolean. */
  onMenuToggle?: (isOpen: boolean) => void;

  /* Callback fired when a menu item is clicked. Receives the item's value. */
  onListItemClick?: (value: string | number) => void;

  /* Optional width for the dropdown menu (e.g., "200px"). */
  width?: string;

  /* If true, shows a checkmark next to the selected item. Defaults to true. */
  showSelectedItemCheckmark?: boolean;
}

/**
 * A dropdown menu component that can be used to display a dropdown menu of items,
 * with a trigger component that opens the menu. You pass in the trigger component,
 * so it can be anything you want, like a button, icon, or div.
 * @param iProps the component props
 * @returns the dropdown menu component
 */
export const DropdownMenu = (iProps: IDropdownMenuProps) => {
  const {
    items,
    renderContent,
    selectedValue,
    leaveOpenUntilClickAway,
    placement = "bottom-start",
    className,
    onMenuToggle,
    triggerComponent,
    width,
    onListItemClick,
    showSelectedItemCheckmark = true,
  } = iProps;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isHoveringRef = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleOpenDropdown = (event: React.MouseEvent<HTMLElement>) => {
    const nextAnchor = anchorEl ? null : event.currentTarget;
    setAnchorEl(nextAnchor);
    if (onMenuToggle) onMenuToggle(Boolean(nextAnchor));
  };

  const handleCloseDropdown = () => {
    setAnchorEl(null);
    if (onMenuToggle) onMenuToggle(false); // Notify parent that menu is closed
  };

  const handleMouseEnter = () => {
    isHoveringRef.current = true;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const handleMouseLeave = () => {
    if (leaveOpenUntilClickAway) return;
    isHoveringRef.current = false;
    setTimeout(() => {
      if (!isHoveringRef.current) handleCloseDropdown();
    }, 100);
  };

  const menuItems = items?.map((item) => (
    <DropdownMenuItem
      showSelectedItemCheckmark={showSelectedItemCheckmark}
      value={item.value}
      isSelected={showSelectedItemCheckmark && item.value === selectedValue}
      key={item.value}
      text={item.text}
      onClick={onListItemClick ?? (() => {})}
    />
  ));

  return (
    <Box
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      display="initial"
    >
      {React.cloneElement(triggerComponent, {
        onClick: handleOpenDropdown,
      })}
      <Popper
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        placement={placement}
        className={classNames(styles.dropdownMenuContainer, className)}
      >
        <ClickAwayListener
          onClickAway={leaveOpenUntilClickAway ? handleCloseDropdown : () => {}}
        >
          <Paper>
            <MenuList
              className={classNames(styles.dropdownMenu)}
              style={{ width }}
            >
              {renderContent?.(handleCloseDropdown)}
              {!renderContent && menuItems}
            </MenuList>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};
